<template>
  <div class="apick relative w100" v-click-outside="close">
    <label v-if="label" class="apick__label">{{ label }}</label>
    <div class="apick__control row align-center" :class="{ 'apick__control--open': show }" :style="controlStyle" @click="open">
      <s-icon class="apick__lead" width="18" height="18" color="grey">
        account-search-outline
      </s-icon>
      <input
        ref="inputRef"
        v-model="query"
        type="text"
        class="apick__input"
        autocomplete="off"
        data-lpignore="true"
        :placeholder="placeholderText"
        @focus="open"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="onEnter"
        @keydown.esc.prevent="close"
      >
      <button type="button" tabindex="-1" class="apick__chev center" @click.stop="toggle">
        <s-icon width="20" height="20" color="grey">
          {{ show ? 'chevron-up' : 'chevron-down' }}
        </s-icon>
      </button>
    </div>

    <transition name="apick-fade">
      <div v-show="show" ref="listRef" class="apick__menu">
        <!-- Invite a brand-new guest by email -->
        <button
          v-if="canAddNew"
          type="button"
          class="apick__opt apick__opt--add"
          :class="{ active: activeIndex === -1 }"
          @mousemove="activeIndex = -1"
          @click="emitAdd"
        >
          <s-icon color="green" height="20">
            account-plus
          </s-icon>
          <span class="ml-10 apick__add-text">Invite &ldquo;{{ query.trim() }}&rdquo; as a new guest</span>
        </button>

        <div v-if="filtered.length === 0" class="apick__empty col center pa-20">
          <span>{{ query ? 'No matching contacts' : 'No saved contacts yet' }}</span>
          <span v-if="query && !canAddNew" class="font-12 opacity-75 mt-5">Enter a valid email to invite someone new</span>
        </div>

        <button
          v-for="(item, index) in filtered"
          :key="item.value"
          type="button"
          class="apick__opt"
          :class="{ selected: item.selected, active: activeIndex === index }"
          @mousemove="activeIndex = index"
          @click="toggleItem(item)"
        >
          <div class="row align-center gap-10 apick__opt-main">
            <s-icon :color="item.selected ? 'primary' : 'blue'">
              {{ item.group ? 'account-group' : 'account' }}
            </s-icon>
            <div class="col apick__opt-text">
              <span class="apick__opt-name">{{ item.display || item.value }}</span>
              <span v-if="item.group && item.attendees" class="apick__opt-sub">{{ item.attendees }}</span>
              <span v-else-if="item.display && item.display !== item.value" class="apick__opt-sub">{{ item.value }}</span>
            </div>
          </div>
          <s-icon v-show="item.selected" class="noPointer" height="16" color="primary">
            check
          </s-icon>
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { validateEmail as validateEmailFunction } from '@/assets/functions';

const props = defineProps({
  modelValue: { type: String, default: '' },
  items: { type: Array, default: () => [] },
  max: { type: [Number, String], default: Infinity },
  label: { type: String, default: '' },
  placeholder: { type: String, default: 'Enter name, email or group name...' },
  height: { type: [Number, String], default: null },
});

const emit = defineEmits(['update:modelValue', 'select', 'add', 'max-reached']);

const show = ref(false);
const activeIndex = ref(-1);
const inputRef = ref(null);
const listRef = ref(null);

// Internal search text, kept in sync with the v-model in both directions.
const query = ref(props.modelValue || '');
watch(() => props.modelValue, (val) => {
  if ((val || '') !== query.value) query.value = val || '';
});
watch(query, (val) => {
  emit('update:modelValue', val);
});

const selectedCount = computed(() => props.items.filter((el) => el.selected).length);

const placeholderText = computed(() => {
  const names = props.items
    .filter((el) => el.selected)
    .map((el) => (el.group ? el.display : (el.display || el.value)))
    .join(', ');
  return names || props.placeholder;
});

const controlStyle = computed(() => {
  const ob = {};
  if (props.height) ob.minHeight = props.height + 'px';
  return ob;
});

const filtered = computed(() => {
  const term = query.value.trim().toLowerCase();
  if (!term) return props.items;
  return props.items.filter((el) => {
    const display = (el.display || '').toLowerCase();
    const value = String(el.value || '').toLowerCase();
    const members = (el.attendees || '').toLowerCase();
    return display.includes(term) || value.includes(term) || members.includes(term);
  });
});

// Offer "invite as new guest" when the query is a valid email that is not
// already one of the saved contacts.
const canAddNew = computed(() => {
  const term = query.value.trim();
  if (!validateEmailFunction(term)) return false;
  const exists = props.items.some(
    (el) => !el.group && String(el.value).toLowerCase() === term.toLowerCase()
  );
  return !exists;
});

const open = () => {
  if (show.value) return;
  show.value = true;
  nextTick(() => inputRef.value && inputRef.value.focus());
};

const close = () => {
  if (!show.value) return;
  show.value = false;
  activeIndex.value = -1;
};

const toggle = () => {
  show.value ? close() : open();
};

const withinMax = () => {
  const max = Number(props.max) || Infinity;
  return selectedCount.value < max;
};

const toggleItem = (item) => {
  if (!item.selected && !withinMax()) {
    emit('max-reached');
    return;
  }
  item.selected = !item.selected;
  emit('select', { value: item.value, display: item.display, selected: item.selected });
  // Reset the search so the full list is easy to browse for the next pick.
  query.value = '';
  activeIndex.value = -1;
  nextTick(() => inputRef.value && inputRef.value.focus());
};

const emitAdd = () => {
  if (!canAddNew.value) return;
  if (!withinMax()) {
    emit('max-reached');
    return;
  }
  emit('add', query.value.trim());
};

const onEnter = () => {
  if (activeIndex.value >= 0 && filtered.value[activeIndex.value]) {
    return toggleItem(filtered.value[activeIndex.value]);
  }
  if (activeIndex.value === -1 && canAddNew.value) return emitAdd();
  if (filtered.value.length === 1) return toggleItem(filtered.value[0]);
  if (canAddNew.value) return emitAdd();
};

const move = (dir) => {
  if (!show.value) return open();
  const min = canAddNew.value ? -1 : 0;
  const maxIdx = filtered.value.length - 1;
  let next = activeIndex.value + dir;
  if (next < min) next = maxIdx;
  if (next > maxIdx) next = min;
  activeIndex.value = next;
  nextTick(scrollActiveIntoView);
};

const scrollActiveIntoView = () => {
  if (!listRef.value) return;
  const el = listRef.value.querySelectorAll('.apick__opt')[
    canAddNew.value ? activeIndex.value + 1 : activeIndex.value
  ];
  if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
};

// expose for parents that want to expose the toggle behaviour on their own item rows
defineExpose({ toggleItem });
</script>

<style scoped lang="less">
.apick__label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  color: var(--black);
}

.apick__control {
  box-sizing: border-box;
  width: 100%;
  min-height: 50px;
  background-color: var(--shade3);
  border: 1.5px solid var(--shade4);
  border-radius: 8px;
  padding: 0 10px 0 14px;
  cursor: text;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.apick__control:hover {
  background-color: var(--shade2);
}

.apick__control--open {
  border-color: var(--primary);
  background-color: var(--shade1);
}

.apick__lead {
  flex: 0 0 auto;
}

.apick__input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 16px;
  color: var(--black);
  padding: 8px;
  height: 100%;
}

.apick__input::placeholder {
  color: var(--shade5);
}

.apick__chev {
  flex: 0 0 auto;
  height: 100%;
  padding: 0 4px;
  background: transparent;
  cursor: pointer;
}

.apick__menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 100%;
  max-width: 100%;
  max-height: 260px;
  overflow-y: auto;
  z-index: 40;
  background-color: var(--shade1);
  border: 1px solid var(--shade4);
  border-radius: 8px;
  box-shadow: 0 4px 14px 0 var(--shade3);
  box-sizing: border-box;
}

.apick__opt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  min-height: 48px;
  background: transparent;
  color: var(--black);
  text-align: start;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.apick__opt.active {
  background-color: var(--shade3);
}

.apick__opt.selected {
  background-color: color-mix(in srgb, var(--primary) 15%, transparent);
  color: var(--black);
}

.apick__opt.selected.active {
  background-color: color-mix(in srgb, var(--primary) 24%, transparent);
}

.apick__opt--add {
  border-bottom: 1px solid var(--shade4);
}

.apick__opt--add.active {
  background-color: var(--shade3);
}

.apick__add-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.apick__opt-main {
  min-width: 0;
  overflow: hidden;
}

.apick__opt-text {
  min-width: 0;
  align-items: flex-start;
  overflow: hidden;
}

.apick__opt-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.apick__opt-sub {
  font-size: 12px;
  opacity: 0.7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.apick__opt.selected .apick__opt-sub {
  opacity: 0.7;
}

.apick__empty {
  color: var(--black);
  text-align: center;
}

.noPointer {
  pointer-events: none;
}

.apick-fade-enter-active,
.apick-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.apick-fade-enter-from,
.apick-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media only screen and (max-width: 576px) {
  .apick__menu {
    max-height: 55vh;
  }
}
</style>
